// Diálogos: busca rápida (Ctrl+K), mover página, lixeira, compartilhar e
// seletor de ícone.

import * as store from "./store.js";
import * as router from "./router.js";
import { api } from "./api.js";
import { el, dialog, popup, menu, icon, titleOf, debounce, toast, relTime, confirmDialog, fmtDateTime } from "./dom.js";
import { iconNode, ui } from "./icons.js";
import { avatar } from "./people.js";

export const newDatabaseName = () => "Novo banco de dados";

const norm = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// Caminho legível ("Área QA / Tasks QA") para mostrar abaixo do título.
export function pathLabel(id) {
  return store.ancestors(id).map((a) => titleOf(a)).join(" / ");
}

// ---------- Busca rápida ----------

export function quickSearch() {
  if (document.querySelector(".dialog.search")) return;
  const input = el("input", { class: "search-input", placeholder: "Buscar páginas, tarefas e reuniões…", autocomplete: "off" });
  const list = el("div", { class: "search-list" });
  let items = [];
  let index = 0;
  let serverQ = "";
  let server = [];

  const local = (q) => {
    const nq = norm(q);
    const out = [];
    for (const p of store.state.tree.values()) {
      const t = norm(p.title);
      if (t.includes(nq)) out.push({ ...p, score: t.startsWith(nq) ? 2 : 1 });
    }
    return out.sort((a, b) => b.score - a.score || b.updated_at - a.updated_at).slice(0, 12);
  };

  const draw = () => {
    const q = input.value.trim();
    if (!q) {
      const recent = store.recent().slice(0, 8);
      items = recent.length ? recent : [...store.state.tree.values()].sort((a, b) => b.updated_at - a.updated_at).slice(0, 8);
    } else {
      const seen = new Set();
      items = [];
      for (const p of [...local(q), ...(serverQ === q ? server : [])]) {
        if (seen.has(p.id)) continue;
        seen.add(p.id);
        items.push(p);
      }
    }
    index = Math.min(index, Math.max(0, items.length - 1));
    list.replaceChildren(
      el("div", { class: "search-label" }, q ? "Resultados" : "Recentes"),
      ...items.map((p, i) => el("button", {
        class: "search-item" + (i === index ? " active" : ""),
        onmousemove: () => { if (index !== i) { index = i; draw(); } },
        onclick: () => pick(p),
      }, el("span", { class: "search-icon" }, icon(p)),
      el("span", { class: "search-text" }, el("span", { class: "search-title" }, titleOf(p)),
        el("span", { class: "search-path" }, p.kind === "row" ? "linha de banco de dados" : pathLabel(p.id))),
      el("span", { class: "search-when" }, p.updated_at ? relTime(p.updated_at) : ""))),
      q && !items.length ? el("p", { class: "search-empty" }, serverQ === q ? "Nada encontrado." : "Buscando…") : null);
    list.querySelector(".active")?.scrollIntoView({ block: "nearest" });
  };

  const fetchServer = debounce(async () => {
    const q = input.value.trim();
    if (q.length < 2) return;
    try {
      const res = await store.search(q);
      if (input.value.trim() !== q) return;
      serverQ = q;
      server = res;
      draw();
    } catch { /* sem conexão: fica só com a busca local */ }
  }, 220);

  let close;
  const pick = (p) => {
    close();
    router.openPage(p.id);
  };
  input.addEventListener("input", () => { index = 0; draw(); fetchServer(); });
  input.addEventListener("keydown", (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); index = Math.min(items.length - 1, index + 1); draw(); }
    if (e.key === "ArrowUp") { e.preventDefault(); index = Math.max(0, index - 1); draw(); }
    if (e.key === "Enter" && items[index]) { e.preventDefault(); pick(items[index]); }
  });
  dialog((c) => {
    close = c;
    return [input, list, el("div", { class: "search-foot" }, "↑↓ para escolher · Enter para abrir · Esc para fechar")];
  }, { className: "search" });
  draw();
  input.focus();
}

// ---------- Mover ----------

export function moveDialog(id) {
  const page = store.node(id);
  if (!page) return;
  const input = el("input", { class: "search-input", placeholder: "Mover para…" });
  const list = el("div", { class: "search-list" });
  let close;
  const candidates = () => {
    const q = norm(input.value.trim());
    return [...store.state.tree.values()]
      .filter((p) => p.kind !== "database" && p.id !== id && !store.isInside(p.id, id) && p.id !== page.parent_id)
      .filter((p) => store.canEdit(p))
      .filter((p) => !q || norm(p.title).includes(q))
      .sort((a, b) => b.updated_at - a.updated_at)
      .slice(0, 50);
  };
  const move = async (parentId) => {
    close();
    const siblings = store.children(parentId);
    await store.patch(id, { parent_id: parentId, position: siblings.length ? siblings.at(-1).position + 1000 : Date.now() }).catch(() => store.loadTree());
    toast(parentId ? `Movida para ${titleOf(store.node(parentId))}.` : "Movida para o topo.");
  };
  const toSpace = async (wsId) => {
    close();
    const name = wsId ? store.workspace(wsId)?.name : "Particular";
    await store.patch(id, { parent_id: null, workspace_id: wsId, position: Date.now() }).catch(() => store.loadTree());
    toast(`Movida para ${wsId ? `o ambiente ${name}` : "o seu Particular"}.`);
  };
  const draw = () => list.replaceChildren(
    el("div", { class: "search-label" }, "Ambientes"),
    ...store.state.workspaces.filter(store.canEditWorkspace).filter((w) => !(page.workspace_id === w.id && !page.parent_id))
      .filter((w) => !input.value.trim() || norm(w.name).includes(norm(input.value.trim())))
      .map((w) => el("button", { class: "search-item", onclick: () => toSpace(w.id) }, el("span", { class: "search-icon" }, iconNode(w.icon, "🗂️")),
        el("span", { class: "search-text" }, el("span", { class: "search-title" }, w.name), el("span", { class: "search-path" }, "Topo do ambiente")))),
    page.parent_id || page.workspace_id ? el("button", { class: "search-item", onclick: () => toSpace(null) }, el("span", { class: "search-icon" }, ui("lock")), el("span", { class: "search-title" }, "Particular (só você)")) : null,
    el("div", { class: "search-label" }, "Páginas"),
    ...candidates().map((p) => el("button", { class: "search-item", onclick: () => move(p.id) },
      el("span", { class: "search-icon" }, icon(p)),
      el("span", { class: "search-text" }, el("span", { class: "search-title" }, titleOf(p)), el("span", { class: "search-path" }, pathLabel(p.id))))));
  input.addEventListener("input", draw);
  dialog((c) => { close = c; return [el("h3", {}, `Mover "${titleOf(page)}"`), input, list]; }, { className: "search" });
  draw();
  input.focus();
}

// ---------- Lixeira ----------

export async function trashDialog() {
  const list = el("div", { class: "search-list" }, el("p", { class: "search-empty" }, "Carregando…"));
  // "Esvaziar lixeira": só aparece quando há algo na lixeira.
  const emptyBtn = el("button", { class: "small danger-btn", hidden: true, onclick: () => emptyAll() }, "Esvaziar lixeira");
  let count = 0;
  const draw = async () => {
    try {
      const pages = await store.trash();
      count = pages.length;
      emptyBtn.hidden = !count;
      list.replaceChildren(...(pages.length ? pages.map((p) => el("div", { class: "trash-row" },
        el("span", { class: "search-icon" }, icon(p)),
        el("span", { class: "search-text" }, el("span", { class: "search-title" }, titleOf(p)), el("span", { class: "search-path" }, `Excluída ${relTime(p.archived_at)}`)),
        el("button", { class: "small", onclick: async () => {
          await store.restore(p.id).catch((err) => toast(err.message));
          toast("Restaurada.");
          draw();
        } }, "Restaurar"),
        el("button", { class: "small danger-btn", title: "Apagar para sempre", onclick: async () => {
          if (!(await confirmDialog(`Apagar "${titleOf(p)}" para sempre? Não dá para desfazer.`, { ok: "Apagar", danger: true }))) return;
          await store.destroy(p.id).catch((err) => toast(err.message));
          draw();
        } }, "Apagar")))
        : [el("p", { class: "search-empty" }, "A lixeira está vazia.")]));
    } catch (err) {
      emptyBtn.hidden = true;
      list.replaceChildren(el("p", { class: "search-empty" }, err.message));
    }
  };
  const emptyAll = async () => {
    const what = count === 1 ? "o item da lixeira" : `os ${count} itens da lixeira`;
    if (!(await confirmDialog(
      `Apagar para sempre ${what}, com tudo o que está dentro deles? Não dá para desfazer. ` +
      `Páginas publicadas perdem o link público. Itens em que você só pode ver continuam na lixeira.`,
      { ok: "Esvaziar lixeira", danger: true }))) return;
    emptyBtn.disabled = true;
    try {
      const { deleted, kept } = await store.emptyTrash();
      toast(kept
        ? `${deleted} item(ns) apagado(s). ${kept} ficaram porque você não tem permissão para apagá-los.`
        : `Lixeira esvaziada (${deleted} item(ns) apagado(s)).`);
    } catch (err) {
      toast("Não foi possível esvaziar a lixeira: " + err.message);
    }
    emptyBtn.disabled = false;
    draw();
  };
  dialog(() => [el("div", { class: "trash-head" }, el("h3", {}, "Lixeira"), emptyBtn), list]);
  draw();
}

// ---------- Compartilhar ----------

const SHARE_ROLES = [["edit", "Pode editar", "Edita e cria páginas dentro"], ["view", "Pode ver", "Só lê e comenta"]];
const roleLabel = (r) => ({ owner: "Acesso total", admin: "Acesso total", edit: "Pode editar", view: "Pode ver" })[r] || r;

export function pageUrl(id) {
  return `ordo://page/${id}`;
}

export function copyPageLink(id) {
  navigator.clipboard?.writeText(pageUrl(id))
    .then(() => toast("Link copiado. Quem tem acesso abre a página pelo link; colado numa página do Ordo, vira um link para ela."))
    .catch(() => toast("Não foi possível copiar o link."));
}

// Como o "Compartilhar" do Notion: convidar pessoas (busca por nome ou
// e-mail), escolher o que cada uma pode fazer e ver quem já tem acesso,
// inclusive quem vê pelo ambiente ou por uma página de cima.
export function shareDialog(pageId, anchor) {
  const page = store.node(pageId) || store.cached(pageId)?.page;
  const canEdit = (() => {
    const c = store.cached(pageId);
    if (c?.role) return c.role !== "view";
    return store.canEdit(store.node(pageId));
  })();
  let data = { shares: store.cached(pageId)?.shares || [], access: null };
  const picked = new Map(); // id -> pessoa escolhida para convidar
  let role = "edit";
  let active = 0;

  const chipList = el("span", { class: "share-chip-list" });
  const input = el("input", { class: "share-input", placeholder: "Nome ou e-mail", autocomplete: "off" });
  const chips = el("div", { class: "share-chips", onclick: () => input.focus() }, chipList, input);
  const roleBtn = el("button", { class: "share-role-btn", type: "button" });
  const inviteBtn = el("button", { class: "primary", type: "button" }, "Convidar");
  const suggest = el("div", { class: "share-suggest" });
  const people = el("div", { class: "share-people" });

  const hasAccess = (id) => id === store.state.me.id || id === data.access?.owner?.id || data.shares.some((x) => x.id === id);
  const candidates = () => {
    const q = norm(input.value.trim());
    return (store.state.users || []).filter((u) => !hasAccess(u.id) && !picked.has(u.id))
      .filter((u) => !q || norm(`${u.name} ${u.email || ""}`).includes(q)).slice(0, 8);
  };
  const drawInvite = () => {
    // O campo fica sempre no mesmo lugar (recriar tiraria o foco dele).
    chipList.replaceChildren(...[...picked.values()].map((u) => el("span", { class: "share-chip" }, avatar(u, "sm"), u.name || u.email,
      el("button", { class: "icon-btn tiny", type: "button", title: "Tirar", onclick: () => { picked.delete(u.id); drawInvite(); input.focus(); } }, ui("x", 12)))));
    input.placeholder = picked.size ? "" : "Nome ou e-mail";
    roleBtn.replaceChildren(roleLabel(role), " ", ui("chevronDown", 12));
    const typing = input.value.trim();
    inviteBtn.disabled = !picked.size && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(typing);
    const list = document.activeElement === input || typing ? candidates() : [];
    active = Math.min(active, Math.max(0, list.length - 1));
    suggest.hidden = !list.length && !typing;
    suggest.replaceChildren(...(list.length
      ? list.map((u, i) => el("button", { class: "share-sugg" + (i === active ? " on" : ""), type: "button",
        onmousedown: (e) => e.preventDefault(), onclick: () => pick(u) },
        avatar(u), el("div", { class: "grow" }, el("div", {}, u.name), el("div", { class: "muted small" }, [u.title, u.email].filter(Boolean).join(" · ")))))
      : typing ? [el("p", { class: "muted small share-hint" }, /@/.test(typing)
        ? "Ninguém com esse e-mail entre as pessoas do Ordo. Se a pessoa já tem conta, clique em Convidar."
        : "Ninguém com esse nome. A pessoa precisa ter uma conta no Ordo.")] : []));
  };
  const pick = (u) => { picked.set(u.id, u); input.value = ""; active = 0; drawInvite(); input.focus(); };

  const invite = async () => {
    const typed = input.value.trim();
    const targets = [...picked.values()].map((u) => ({ user_id: u.id }));
    if (!targets.length && typed) targets.push({ email: typed });
    if (!targets.length) return;
    inviteBtn.disabled = true;
    try {
      for (const t of targets) data = { ...data, ...(await api("POST", `/pages/${pageId}/shares`, { ...t, role })) };
      picked.clear();
      input.value = "";
      store.invalidate(pageId);
      drawInvite();
      drawPeople();
      toast(targets.length > 1 ? `${targets.length} pessoas convidadas.` : "Convite enviado. A pessoa recebe um aviso na caixa de entrada.");
    } catch (err) {
      toast(err.message);
      drawInvite();
    }
  };

  const roleMenu = (btn, cur, onPick, { remove } = {}) => menu(btn, [
    ...SHARE_ROLES.map(([v, l, h]) => ({ label: el("span", { class: "role-opt" }, el("strong", {}, l), el("span", { class: "muted small" }, h)), icon: v === cur ? ui("check", 14) : el("span", { class: "menu-gap" }), run: () => onPick(v) })),
    remove ? "-" : null,
    remove ? { label: "Remover acesso", danger: true, run: remove } : null,
  ].filter(Boolean), { align: "right", nested: true });

  const row = (u, right, sub) => el("div", { class: "share-row" }, avatar(u, "md"),
    el("div", { class: "grow share-who" }, el("div", {}, u.name, u.id === store.state.me.id ? el("span", { class: "muted" }, " (você)") : null),
      el("div", { class: "muted small" }, sub ?? [u.title, u.email].filter(Boolean).join(" · "))),
    right);

  const drawPeople = () => {
    const a = data.access;
    const hit = store.cached(pageId);
    if (hit && hit.shares !== data.shares && hit.role !== "view") { hit.shares = data.shares; store.emit("tree"); }
    const rows = [];
    if (a?.owner && !a.workspace) rows.push(row(a.owner, el("span", { class: "muted small" }, "Dono da página")));
    if (a?.workspace) {
      const w = a.workspace;
      const others = w.members.filter((m) => !data.shares.some((x) => x.id === m.id));
      rows.push(el("div", { class: "share-row" }, el("span", { class: "share-space" }, iconNode(w.icon, "🗂️")),
        el("div", { class: "grow share-who" }, el("div", {}, `Todos em ${w.name}`),
          el("div", { class: "muted small" }, `${w.members.length} ${w.members.length === 1 ? "membro" : "membros"} do ambiente`)),
        el("span", { class: "avatar-stack", title: others.map((m) => `${m.name}: ${roleLabel(m.role === "admin" ? "owner" : m.role)}`).join("\n") },
          others.slice(0, 4).map((m) => avatar(m, "sm")))));
    }
    for (const sh of data.shares) {
      rows.push(row(sh, canEdit
        ? el("button", { class: "share-role-btn", type: "button", onclick: (e) => roleMenu(e.currentTarget, sh.role, async (v) => {
          try { data = { ...data, ...(await api("POST", `/pages/${pageId}/shares`, { user_id: sh.id, role: v })) }; drawPeople(); } catch (err) { toast(err.message); }
        }, { remove: async () => {
          try { data = { ...data, ...(await api("DELETE", `/pages/${pageId}/shares/${sh.id}`)) }; store.invalidate(pageId); drawPeople(); drawInvite(); } catch (err) { toast(err.message); }
        } }) }, roleLabel(sh.role), " ", ui("chevronDown", 12))
        : el("span", { class: "muted small" }, roleLabel(sh.role))));
    }
    const seen = new Set(data.shares.map((x) => x.id));
    for (const ih of a?.inherited || []) {
      if (seen.has(ih.id)) continue;
      seen.add(ih.id);
      rows.push(row(ih, el("span", { class: "muted small" }, roleLabel(ih.role)),
        el("span", {}, "pela página ", el("button", { class: "link small", type: "button", onclick: () => { box.close(); router.openPage(ih.from.id); } }, titleOf(ih.from)))));
    }
    people.replaceChildren(
      el("div", { class: "share-label" }, "Pessoas com acesso"),
      ...(rows.length ? rows : [el("p", { class: "muted small" }, a ? "Ninguém além de quem já participa." : "Carregando…")]),
      a && !a.workspace && !data.shares.length && !(a.inherited || []).length
        ? el("p", { class: "muted small share-hint" }, "Só você vê esta página. Quem você convidar vê ela e tudo que está dentro.") : null);
  };

  input.addEventListener("input", () => { active = 0; drawInvite(); });
  input.addEventListener("focus", drawInvite);
  input.addEventListener("blur", () => setTimeout(drawInvite, 100));
  input.addEventListener("keydown", (e) => {
    const list = candidates();
    if (e.key === "ArrowDown") { e.preventDefault(); active = Math.min(active + 1, list.length - 1); drawInvite(); }
    else if (e.key === "ArrowUp") { e.preventDefault(); active = Math.max(active - 1, 0); drawInvite(); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (list[active] && input.value.trim()) pick(list[active]);
      else invite();
    } else if (e.key === "Backspace" && !input.value && picked.size) {
      picked.delete([...picked.keys()].pop());
      drawInvite();
    }
  });
  roleBtn.addEventListener("click", () => roleMenu(roleBtn, role, (v) => { role = v; drawInvite(); }));
  inviteBtn.addEventListener("click", invite);

  const publish = publishSection(pageId, () => box);
  const box = popup(anchor || document.querySelector(".topbar .share-btn") || { x: innerWidth - 40, y: 48 }, el("div", { class: "share-pop" },
    el("div", { class: "share-head" }, el("strong", {}, "Compartilhar"), el("span", { class: "muted small grow share-title" }, titleOf(page))),
    canEdit ? el("div", { class: "share-invite" }, chips, roleBtn, inviteBtn) : el("p", { class: "muted small" }, "Você pode ver esta página, mas só quem edita pode convidar pessoas."),
    canEdit ? suggest : null,
    people,
    publish.box,
    el("div", { class: "share-foot" },
      el("span", { class: "muted small grow" }, "O acesso vale para as páginas de dentro também."),
      el("button", { class: "small", type: "button", onclick: () => copyPageLink(pageId) }, ui("link", 13), " Copiar link"))),
  { align: "right", className: "share-popup" });
  drawInvite();
  drawPeople();
  publish.load();
  // Servidor antigo (sem "access"): mostra ao menos você e os convidados.
  api("GET", `/pages/${pageId}/shares`).then((r) => { data = { access: { owner: null, workspace: null, inherited: [] }, ...r }; drawPeople(); drawInvite(); })
    .catch((err) => people.replaceChildren(el("p", { class: "error small" }, err.message)));
  if (canEdit) requestAnimationFrame(() => input.focus());
  return box;
}

// ---------- Publicar na internet ----------

// Seção "Publicar na web" do Compartilhar: liga um link público (abre no
// navegador de qualquer pessoa, sem login), com as opções de incluir as páginas
// de dentro e de deixar buscadores encontrarem. Só o dono da página (ou quem
// administra o ambiente) muda; os outros veem a situação e o link.
function publishSection(pageId, getBox) {
  const box = el("div", { class: "publish" });
  let state = null; // {publication, can_publish}
  let busy = false;

  const save = async (method, body) => {
    if (busy) return;
    busy = true;
    draw();
    try {
      state = await api(method, `/pages/${pageId}/publish`, body);
      const hit = store.cached(pageId);
      if (hit) { hit.publication = state.publication; store.emit("tree"); }
      if (method === "PUT" && !body?.keep) toast("Página publicada. Qualquer pessoa com o link consegue ler.");
    } catch (err) {
      toast(err.message);
    } finally {
      busy = false;
      draw();
    }
  };
  const copy = (url) => navigator.clipboard?.writeText(url).then(() => toast("Link público copiado.")).catch(() => toast("Não foi possível copiar o link."));
  const open = (url) => window.open(url, "_blank");
  const linkRow = (url) => el("div", { class: "publish-link" },
    el("input", { class: "grow", readonly: true, value: url, onfocus: (e) => e.target.select() }),
    el("button", { class: "small", type: "button", onclick: () => copy(url) }, ui("copy", 13), " Copiar"),
    el("button", { class: "small", type: "button", title: "Abrir no navegador", onclick: () => open(url) }, ui("open", 13)));
  const option = (label, hint, checked, onchange) => el("label", { class: "publish-opt" },
    el("input", { type: "checkbox", checked, disabled: busy, onchange: (e) => onchange(e.target.checked) }),
    el("span", { class: "grow" }, el("div", {}, label), el("div", { class: "muted small" }, hint)));

  const draw = () => {
    const head = el("div", { class: "share-label" }, "Publicar na web");
    if (!state) { box.replaceChildren(head, el("p", { class: "muted small" }, "Carregando…")); return; }
    const pub = state.publication;
    const can = state.can_publish;
    const who = pub?.published_by ? ` por ${pub.published_by.name}` : "";
    const when = pub ? `Publicada${who} ${relTime(pub.published_at)}${pub.views ? ` · ${pub.views} ${pub.views === 1 ? "visita" : "visitas"}` : ""}` : "";

    // Publicada junto com uma página de cima: as opções ficam lá.
    if (pub?.inherited) {
      box.replaceChildren(head,
        el("div", { class: "publish-status on" }, ui("globe", 16),
          el("span", { class: "grow" }, "Está na internet junto com a página ",
            el("button", { class: "link", type: "button", onclick: () => { getBox()?.close(); router.openPage(pub.inherited.id); } }, pub.inherited.title || "Sem título"), ".")),
        linkRow(pub.url));
      return;
    }
    if (!pub) {
      box.replaceChildren(head,
        el("div", { class: "publish-status" }, ui("globe", 16),
          el("span", { class: "grow" }, el("div", {}, "Não publicada"),
            el("div", { class: "muted small" }, can
              ? "Gera um link que abre no navegador de qualquer pessoa, sem login. Ela só lê; não edita nem comenta."
              : "Só o dono da página ou quem administra o ambiente pode publicar.")),
          can ? el("button", { class: "primary small", type: "button", disabled: busy, onclick: () => save("PUT", { subpages: true }) }, busy ? "Publicando…" : "Publicar") : null));
      return;
    }
    box.replaceChildren(head,
      el("div", { class: "publish-status on" }, ui("globe", 16),
        el("span", { class: "grow" }, el("div", {}, "Publicada na internet"), el("div", { class: "muted small" }, when))),
      linkRow(pub.url),
      can ? option("Incluir as páginas de dentro", "Subpáginas e cards dos bancos abrem pelo mesmo link.", pub.subpages, (v) => save("PUT", { subpages: v, keep: true })) : null,
      can ? option("Deixar buscadores encontrarem", "O Google e outros buscadores podem listar a página.", pub.indexable, (v) => save("PUT", { indexable: v, keep: true })) : null,
      can
        ? el("div", { class: "publish-actions" }, el("button", { class: "small danger-btn", type: "button", disabled: busy, onclick: async () => {
          if (await confirmDialog("Tirar esta página da internet? O link público para de funcionar na hora. Se publicar de novo, o link será outro.", { ok: "Despublicar", danger: true })) save("DELETE");
        } }, "Despublicar"))
        : el("p", { class: "muted small" }, "Só o dono da página ou quem administra o ambiente pode mudar ou despublicar."));
  };

  const load = () => api("GET", `/pages/${pageId}/publish`).then((r) => { state = r; draw(); })
    .catch(() => box.replaceChildren()); // servidor antigo: sem a seção
  draw();
  return { box, load };
}

// ---------- Ícone ----------

const EMOJIS = "📄 📝 📌 📎 📁 🗂️ 📊 📈 📅 🗓️ ✅ ☑️ 🧪 🐞 🛠️ ⚙️ 🚀 💡 🎯 🔥 ⭐ 📣 🎙️ 💬 🤝 👥 👤 🏠 🏢 💼 💰 🧾 📦 🔒 🔑 🌐 💻 📱 🖥️ 🧩 🧠 📚 📖 ✏️ 🖊️ 🔍 ⚠️ ❗ ❓ ✨ 🎉 🟢 🟡 🔴 🔵 🟣 ⚪ ⚫ 🩺 🏥 💊 🚑 🎓 🛒 🧭 🗺️ 🕒".split(" ");

export function emojiPicker(anchor, onPick) {
  const custom = el("input", { class: "pop-input", placeholder: "Ou cole um emoji", maxlength: 8 });
  const box = popup(anchor, el("div", { class: "emoji-pop" },
    el("div", { class: "emoji-grid" }, EMOJIS.map((e) => el("button", { class: "emoji", onclick: () => { box.close(); onPick(e); } }, e))),
    el("div", { class: "pop-row" }, custom,
      el("button", { class: "link", onclick: () => { box.close(); onPick(null); } }, "Remover"))));
  custom.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && custom.value.trim()) { box.close(); onPick(custom.value.trim()); }
  });
  return box;
}

// ---------- Escolher uma página (link para página) ----------

export function pagePicker(onPick, excludeId, { kind } = {}) {
  const input = el("input", { class: "search-input", placeholder: kind === "database" ? "Mostrar qual banco de dados?" : "Ligar a qual página?" });
  const list = el("div", { class: "search-list" });
  let close;
  const draw = () => {
    const q = norm(input.value.trim());
    const items = [...store.state.tree.values()]
      .filter((p) => p.id !== excludeId && (!kind || p.kind === kind) && (!q || norm(p.title).includes(q)))
      .sort((a, b) => b.updated_at - a.updated_at).slice(0, 40);
    list.replaceChildren(...items.map((p) => el("button", { class: "search-item", onclick: () => { close(); onPick(p.id); } },
      el("span", { class: "search-icon" }, icon(p)),
      el("span", { class: "search-text" }, el("span", { class: "search-title" }, titleOf(p)), el("span", { class: "search-path" }, pathLabel(p.id))))));
  };
  input.addEventListener("input", draw);
  dialog((c) => { close = c; return [input, list]; }, { className: "search" });
  draw();
  input.focus();
}

// ---------- Enviar para ----------

export function sendDialog(pageId) {
  const page = store.node(pageId) || store.cached(pageId)?.page;
  const chosen = new Set();
  const list = el("div", { class: "people-pick" });
  const msg = el("textarea", { rows: 2, placeholder: "Mensagem (opcional)" });
  const draw = () => list.replaceChildren(...(store.state.users || []).filter((u) => u.id !== store.state.me.id).map((u) => el("label", { class: "person-row" },
    el("input", { type: "checkbox", checked: chosen.has(u.id), onchange: (e) => { if (e.target.checked) chosen.add(u.id); else chosen.delete(u.id); } }),
    avatar(u, "sm"), el("span", {}, u.name), el("span", { class: "muted small" }, u.email || ""))));
  draw();
  dialog((close) => [
    el("h3", {}, `Enviar "${titleOf(page)}"`),
    el("p", { class: "muted small" }, "A pessoa recebe um aviso na Caixa de entrada. Quem ainda não tem acesso passa a poder ver a página."),
    list, msg,
    el("div", { class: "dlg-actions" }, el("button", { onclick: close }, "Cancelar"),
      el("button", { class: "primary", onclick: async () => {
        if (!chosen.size) return toast("Escolha pelo menos uma pessoa.");
        try {
          const res = await api("POST", `/pages/${pageId}/send`, { user_ids: [...chosen], message: msg.value.trim() });
          close();
          toast(`Enviada para ${res.sent} pessoa(s).`);
        } catch (err) { toast(err.message); }
      } }, "Enviar")),
  ]);
}

// ---------- Lembrete ----------

export async function reminderDialog(pageId) {
  const page = store.node(pageId) || store.cached(pageId)?.page;
  const pad = (n) => String(n).padStart(2, "0");
  const t = new Date(Date.now() + 86400_000);
  t.setHours(9, 0, 0, 0);
  const when = el("input", { type: "datetime-local", value: `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}T09:00` });
  const text = el("input", { placeholder: "Sobre o quê? (opcional)" });
  const list = el("div", { class: "reminder-list" });
  const draw = async () => {
    try {
      const { reminders } = await api("GET", `/reminders?page_id=${pageId}`);
      list.replaceChildren(...reminders.filter((r) => !r.sent_at).map((r) => el("div", { class: "reminder-row" },
        ui("bell", 14), el("span", { class: "grow" }, fmtDateTime(r.at), r.text ? ` · ${r.text}` : ""),
        el("button", { class: "icon-btn tiny", title: "Cancelar lembrete", onclick: async () => { await api("DELETE", `/reminders/${r.id}`); draw(); } }, ui("x", 14)))));
    } catch { /* sem conexão */ }
  };
  draw();
  dialog((close) => [
    el("h3", {}, `Lembrete: ${titleOf(page)}`),
    el("p", { class: "muted small" }, "No horário, o aviso aparece na sua Caixa de entrada (e como notificação do Windows, se o Ordo estiver aberto)."),
    el("label", { class: "field" }, "Quando", when),
    el("label", { class: "field" }, "Nota", text),
    list,
    el("div", { class: "dlg-actions" }, el("button", { onclick: close }, "Fechar"),
      el("button", { class: "primary", onclick: async () => {
        const at = new Date(when.value).getTime();
        try {
          await api("POST", "/reminders", { page_id: pageId, at, text: text.value.trim() });
          toast(`Lembrete marcado para ${fmtDateTime(at)}.`);
          close();
        } catch (err) { toast(err.message); }
      } }, "Marcar lembrete")),
  ]);
}

// ---------- Histórico de versões ----------

export async function versionsDialog(pageId, onRestored) {
  const list = el("div", { class: "versions-list" }, el("p", { class: "muted" }, "Carregando…"));
  const preview = el("div", { class: "version-preview" }, el("p", { class: "muted" }, "Escolha uma versão à esquerda para ver como a página estava."));
  let chosen = null;
  const restoreBtn = el("button", { class: "primary", disabled: true }, "Restaurar esta versão");
  restoreBtn.addEventListener("click", async () => {
    if (!chosen) return;
    if (!(await confirmDialog("Restaurar esta versão? O texto atual vira uma nova versão no histórico, então dá para voltar.", { ok: "Restaurar" }))) return;
    await store.patch(pageId, { title: chosen.title || "", content: chosen.content || null }).catch(() => {});
    store.invalidate(pageId);
    dlg.close();
    onRestored?.();
    toast("Versão restaurada.");
  });
  const dlg = dialog((close) => [
    el("h3", {}, ui("history", 18), " Histórico de versões"),
    el("div", { class: "versions" }, list, preview),
    el("div", { class: "dlg-actions" }, el("button", { onclick: close }, "Fechar"), restoreBtn),
  ], { wide: true, className: "versions-dialog" });
  try {
    const { versions } = await api("GET", `/pages/${pageId}/versions`);
    if (!versions.length) {
      list.replaceChildren(el("p", { class: "muted small" }, "Ainda não há versões antigas. O Ordo guarda uma versão a cada 10 minutos de edição."));
      return;
    }
    list.replaceChildren(...versions.map((v) => el("button", { class: "version-item", onclick: async (e) => {
      for (const b of list.querySelectorAll(".on")) b.classList.remove("on");
      e.currentTarget.classList.add("on");
      const { version } = await api("GET", `/pages/${pageId}/versions/${v.id}`);
      chosen = version;
      restoreBtn.disabled = false;
      const host = el("div", { class: "editor readonly" });
      preview.replaceChildren(el("h2", {}, version.title || "Sem título"), host);
      const { createEditor } = await import("./editor.js");
      const ed = createEditor(host, { content: version.content, editable: false, ctx: { pageId } });
      dlg.addEventListener("close", () => ed.destroy(), { once: true });
    } }, el("strong", {}, fmtDateTime(v.at)), el("span", { class: "muted small" }, v.user_name || ""))));
  } catch (err) {
    list.replaceChildren(el("p", { class: "error" }, err.message));
  }
}
